// Loads bounded pages of published stories. Only topic OR audience is sent to
// Firestore (plus an optional date lower bound); type/search/the other filter
// are refined client-side over what is loaded. Pages are cached per query key
// for the session so toggling filters back and forth doesn't re-read.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Story } from "../../shared/model.ts";
import { refine, serverFilterFor, sinceFor, type Filters } from "../lib/filters.ts";
import type { Cursor, StoryRepository } from "../lib/repository.ts";

interface PageState {
  stories: Story[];
  cursor: Cursor | null;
  hasMore: boolean;
}

export type LoadStatus = "loading" | "ready" | "loading_more" | "error";

const pageCache = new Map<string, PageState>();
const countCache = new Map<string, number>();
const DAY = 86_400_000;

export function useStories(repo: StoryRepository | null, filters: Filters, onLoaded: (s: Story[]) => void) {
  const serverFilter = serverFilterFor(filters);
  // Round "since" to the day so the cache key is stable within a session.
  const today = Math.floor(Date.now() / DAY) * DAY;
  const since = sinceFor(filters.dateRange, today);
  const key = JSON.stringify([serverFilter, since]);

  const [state, setState] = useState<PageState>(() => pageCache.get(key) ?? { stories: [], cursor: null, hasMore: false });
  const [status, setStatus] = useState<LoadStatus>(pageCache.has(key) ? "ready" : "loading");
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState<number | null>(countCache.get(key) ?? null);
  // Server count for the indexed part of the query (topic/audience + date), cached per key.
  useEffect(() => {
    if (!repo) return;
    const cached = countCache.get(key);
    if (cached !== undefined) return setTotal(cached);
    setTotal(null);
    let alive = true;
    repo
      .countPublished(serverFilter, since)
      .then((n) => {
        countCache.set(key, n);
        if (alive) setTotal(n);
      })
      .catch(() => alive && setTotal(null));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repo, key]);
  const keyRef = useRef(key);
  keyRef.current = key;

  const load = useCallback(
    async (append: boolean) => {
      if (!repo) return;
      const k = keyRef.current;
      const prev = append ? pageCache.get(k) : undefined;
      setStatus(append ? "loading_more" : "loading");
      setError(null);
      try {
        const page = await repo.listPage(serverFilter, since, prev?.cursor ?? null);
        const next: PageState = {
          stories: [...(prev?.stories ?? []), ...page.stories],
          cursor: page.cursor,
          hasMore: page.hasMore,
        };
        pageCache.set(k, next);
        onLoaded(page.stories);
        if (keyRef.current === k) {
          setState(next);
          setStatus("ready");
        }
      } catch (err) {
        console.error(err);
        if (keyRef.current === k) {
          setError(err instanceof Error ? err.message : "Something went wrong loading updates.");
          setStatus("error");
        }
      }
    },
    // serverFilter/since are derived from key
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [repo, key, onLoaded],
  );

  useEffect(() => {
    const cached = pageCache.get(key);
    if (cached) {
      setState(cached);
      setStatus("ready");
      onLoaded(cached.stories);
      return;
    }
    setState({ stories: [], cursor: null, hasMore: false });
    void load(false);
  }, [key, load, onLoaded]);

  const visible = useMemo(() => refine(state.stories, filters), [state.stories, filters]);

  return {
    loaded: state.stories,
    visible,
    hasMore: state.hasMore,
    status,
    error,
    loadMore: () => load(true),
    retry: () => load(false),
    serverFilterKind: serverFilter.kind,
    /** Published updates matching topic/audience/date on the server (null while loading). */
    total,
  };
}
